import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Diamond, Lock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset! Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <Diamond className="w-6 h-6 text-zinc-900" strokeWidth={1.5} />
          <span className="font-display text-lg font-semibold text-zinc-900">Shinning Eyes</span>
        </div>

        {!token ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-6 text-center" data-testid="reset-invalid">
            <h2 className="font-display text-xl font-semibold text-zinc-900">Invalid link</h2>
            <p className="text-sm text-zinc-500 mt-2">This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="inline-flex items-center gap-1 text-sm text-zinc-900 font-medium mt-5 hover:underline">
              Request a new link
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">Set new password</p>
              <h2 className="font-display text-3xl font-semibold text-zinc-900">Reset password</h2>
              <p className="text-sm text-zinc-500 mt-2">Choose a new password for your account.</p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="pw" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">New password</Label>
                <Input id="pw" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw2" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Confirm password</Label>
                <Input id="pw2" type="password" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-11" disabled={loading} data-testid="reset-submit-button">
                <Lock className="w-4 h-4 mr-2" />
                {loading ? "Resetting…" : "Reset password"}
              </Button>
            </form>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-zinc-600 mt-6 hover:text-zinc-900">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
